import { useMemo, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { AIRCRAFT_TYPES, aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import { forecastRoute } from '../simulation/economy';
import { idleDeliveredAircraft } from '../simulation/leasing';
import type { GameState } from '../game/types';
import { integer, kilometers, money, percent } from '../ui/format';
import { useEscToClose } from '../hooks/useEscToClose';
import { useI18n } from '../i18n';

interface Props {
  game: GameState;
  originId: string;
  destinationId: string;
  preferredAircraftId?: string;
  onClose: () => void;
  onConfirm: (aircraftId: string) => void;
}

export function RoutePlanner({ game, originId, destinationId, preferredAircraftId, onClose, onConfirm }: Props) {
  const { t } = useI18n();
  useEscToClose(() => [{ open: true, close: onClose }]);
  const idleAircraft = useMemo(() => idleDeliveredAircraft(game)
    .sort((a, b) => (a.id === preferredAircraftId ? -1 : 0) - (b.id === preferredAircraftId ? -1 : 0)), [game, preferredAircraftId]);
  const forecasts = useMemo(() => idleAircraft.map((aircraft) => ({
    aircraft,
    type: aircraftType(aircraft.typeId),
    forecast: forecastRoute(game, originId, destinationId, aircraft),
  })), [destinationId, game, idleAircraft, originId]);
  const origin = airportById(originId);
  const destination = airportById(destinationId);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section className="planner-sheet" role="dialog" aria-modal="true" aria-label={t('ui.航线投资预测')} onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <header className="sheet-header">
          <div>
            <span className="eyebrow">{t('ui.航线投资预测')}</span>
            <h2>{origin.nameZh} <b>→</b> {destination.nameZh}</h2>
            <p>{origin.iata} · {destination.iata}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={t('ui.关闭')}><X weight="bold" size={22} /></button>
        </header>
        <section className="forecast-city-profile" data-tutorial-destination-profile aria-label={`${destination.nameZh}${t('ui.城市画像')}`}>
          <div className="forecast-city-profile-heading">
            <div><span className="eyebrow">{t('ui.目标城市画像')}</span><strong>{destination.nameZh}</strong></div>
            <span>{destination.iata} · {destination.country}</span>
          </div>
          <div className="forecast-city-metrics">
            {[
              { id: 'market', label: t('ui.城市规模'), value: destination.market },
              { id: 'business', label: t('ui.商务指数'), value: destination.business },
              { id: 'tourism', label: t('ui.旅游指数'), value: destination.tourism },
              { id: 'international', label: t('ui.国际化'), value: destination.international },
            ].map(({ id, label, value }) => (
              <div className="forecast-city-metric" data-tutorial-city-metric={id} key={label}>
                <span>{label}</span>
                <div><i style={{ width: `${Number(value) * 10}%` }} /></div>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </section>
        {!idleAircraft.length ? (
          <div className="empty-state compact">
            <strong>{t('ui.没有空闲飞机')}</strong>
            <p>{t('ui.先购买飞机，或关闭一条航线释放运力。')}</p>
          </div>
        ) : (
          <div className="forecast-list">
            {forecasts.map(({ aircraft, type, forecast }) => {
              const valid = forecast.rangeCompatible && forecast.runwayCompatible && game.cash >= forecast.launchCost;
              return (
                <article className={`forecast-card ${valid ? '' : 'disabled'}`} key={aircraft.id}>
                  <div className="forecast-aircraft">
                    <i style={{ background: type.accent }} />
                    <div>
                      <strong>{type.name}</strong>
                      <span>{t(aircraft.lease ? 'ui.租赁' : aircraft.acquisition === 'used' ? 'ui.二手' : 'ui.新机')} · {t('ui.航程')} {kilometers(type.rangeKm)} · {t('ui.状态')} {Math.round(aircraft.condition * 100)}%</span>
                    </div>
                    <span>{kilometers(forecast.distanceKm)}</span>
                  </div>
                  <div className="forecast-kpis">
                    <div><span>{t('ui.开航投入')}</span><strong>{money(forecast.launchCost)}</strong></div>
                    <div><span>{t('ui.预计需求')}</span><strong>{integer(forecast.demand)}</strong></div>
                    <div><span>{t('ui.周座位')}</span><strong>{integer(forecast.seats)}</strong></div>
                    <div><span>{t('ui.预计载客率')}</span><strong className={forecast.loadFactor >= 0.98 ? 'gold' : ''}>{percent(forecast.loadFactor)}</strong></div>
                    <div><span>{t('ui.周经营利润')}</span><strong className={forecast.weeklyProfit < 0 ? 'red' : 'green'}>{money(forecast.weeklyProfit)}</strong></div>
                    <div><span>{t('ui.项目总回本')}</span><strong>{forecast.paybackWeeks ? t('ui.{weeks}周', { weeks: forecast.paybackWeeks }) : t('ui.无法回本')}</strong></div>
                  </div>
                  <div className="forecast-cabins">
                    <div><span>{t('ui.经济舱')}</span><strong>{integer(forecast.cabins.economy.seats)} {t('ui.座')} · {integer(forecast.cabins.economy.demand)} {t('ui.需求')}</strong></div>
                    <div><span>{t('ui.商务舱')}</span><strong>{integer(forecast.cabins.business.seats)} {t('ui.座')} · {integer(forecast.cabins.business.demand)} {t('ui.需求')}</strong></div>
                    <div><span>{t('ui.头等舱')}</span><strong>{integer(forecast.cabins.first.seats)} {t('ui.座')} · {integer(forecast.cabins.first.demand)} {t('ui.需求')}</strong></div>
                  </div>
                  <div className="forecast-reasons">
                    {(forecast.reasons.length ? forecast.reasons : [t('ui.供需与机型匹配处于正常区间')]).slice(0, 3).map((reason) => <span key={reason}>· {reason}</span>)}
                  </div>
                    <button data-tutorial-route-confirm className="primary-button" disabled={!valid} onClick={() => onConfirm(aircraft.id)}>
                    {!forecast.rangeCompatible ? t('ui.航程不足') : !forecast.runwayCompatible ? t('ui.跑道不兼容') : game.cash < forecast.launchCost ? t('ui.现金不足') : t('ui.投入 {amount} 开航', { amount: money(forecast.launchCost) })}
                  </button>
                </article>
              );
            })}
          </div>
        )}
        <div className="formula-note">{t('ui.预测按新航线55%的初始成熟度计算；回本包含飞机账面资本与开航投入；客流永远受真实座位上限约束。')}</div>
        <div className="aircraft-reference" aria-hidden="true">
          {AIRCRAFT_TYPES.slice(0, 4).map((type) => <i key={type.id} style={{ background: type.accent }} />)}
        </div>
      </section>
    </div>
  );
}
